<template>
  <section class="invoice-preview-wrapper itf-print-wrapper">
    <b-alert
      variant="danger"
      :show="!invoiceData"
    >
      <h4 class="alert-heading">
        ITF տվյալները չեն գտնվել
      </h4>
    </b-alert>

    <b-row
      v-if="invoiceData"
      class="invoice-preview"
    >
      <b-col
        cols="12"
        xl="9"
        md="8"
        class="itf-print-main"
      >
        <b-card
          no-body
          class="invoice-preview-card"
        >
          <div id="itf-print-root">
            <ItfPrintContent
              :invoice-data="invoiceData"
              :lang="printLang"
              :classificators="classificators"
            />
          </div>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <b-form-group
            class="mb-75"
            label="Լեզու"
          >
            <b-form-select
              v-model="printLang"
              :options="langOptions"
            />
          </b-form-group>

          <b-button
            variant="outline-secondary"
            class="mb-75"
            block
            @click="closeDoc"
          >
            Փակել
          </b-button>
          <b-button
            variant="success"
            class="mb-75"
            block
            @click="printInvoice"
          >
            Տպել
          </b-button>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormSelect,
  BRow,
} from 'bootstrap-vue'
import store from '@/store'
import itfModule from '@/store/itf/itf'
import ItfPrintContent from '@/views/pages/itf/docs/ItfPrintContent.vue'
import { getItfRecordId } from '@/utils/itfRecordId'

const ITF_PRINT_PAGE_STYLE_ID = 'itf-print-page-style'

export default {
  name: 'ItfPrint',
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormSelect,
    BRow,
    ItfPrintContent,
  },
  data() {
    return {
      ITF_STORE_MODULE_NAME: 'itfModule',
      printLang: 'en',
      langOptions: [
        { value: 'en', text: 'English' },
        { value: 'hy', text: 'Հայերեն' },
      ],
    }
  },
  computed: {
    invoiceData() {
      return this.$store.state[this.ITF_STORE_MODULE_NAME].selectedItf
    },
    classificators() {
      return this.$store.state[this.ITF_STORE_MODULE_NAME].classificators || []
    },
    itfRecordId() {
      return getItfRecordId(this.invoiceData)
    },
  },
  created() {
    if (!store.hasModule(this.ITF_STORE_MODULE_NAME)) {
      store.registerModule(this.ITF_STORE_MODULE_NAME, itfModule)
    }
    this.$store.dispatch(`${this.ITF_STORE_MODULE_NAME}/getClassificators`).catch(() => {})
    const id = this.itfRecordId
    if (id) {
      this.$store.dispatch(`${this.ITF_STORE_MODULE_NAME}/getItf`, id).catch(() => {})
    }
  },
  beforeDestroy() {
    this.endItfPrintMode()
  },
  destroyed() {
    this.$store.commit(`${this.ITF_STORE_MODULE_NAME}/CLOSE_DOC`, 'print')
  },
  methods: {
    closeDoc() {
      this.endItfPrintMode()
      this.$store.commit(`${this.ITF_STORE_MODULE_NAME}/CLOSE_DOC`, 'print')
      this.$store.commit(`${this.ITF_STORE_MODULE_NAME}/SET_SELECTED_ITF`, null)
      this.$emit('closed')
    },
    startItfPrintMode() {
      document.documentElement.classList.add('itf-printing')
      document.body.classList.add('itf-printing')

      if (!document.getElementById(ITF_PRINT_PAGE_STYLE_ID)) {
        const style = document.createElement('style')
        style.id = ITF_PRINT_PAGE_STYLE_ID
        style.textContent = '@page { size: A4 portrait; margin: 0; }'
        document.head.appendChild(style)
      }
    },
    endItfPrintMode() {
      document.documentElement.classList.remove('itf-printing')
      document.body.classList.remove('itf-printing')

      const style = document.getElementById(ITF_PRINT_PAGE_STYLE_ID)
      if (style) style.remove()
      window.removeEventListener('afterprint', this.endItfPrintMode)
    },
    printInvoice() {
      this.startItfPrintMode()
      window.addEventListener('afterprint', this.endItfPrintMode)
      window.print()
    },
  },
}
</script>

<style lang="scss">
.itf-print-wrapper {
  .invoice-preview-card {
    overflow: auto;
  }
}

/* ITF-only print rules — active only while html/body have .itf-printing */
@media print {
  html.itf-printing {
    width: 100% !important;
    height: auto !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  html.itf-printing body.itf-printing {
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: auto !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  html.itf-printing {
    nav.header-navbar,
    .main-menu,
    .header-navbar-shadow,
    footer.footer,
    .customizer-toggle {
      display: none !important;
    }

    .content.app-content {
      margin: 0 !important;
      padding: 0 !important;
    }

    .card {
      background: transparent !important;
      box-shadow: none !important;
    }
  }

  html.itf-printing .itf-print-wrapper {
    width: 210mm !important;
    max-width: 210mm !important;
    margin: 0 auto !important;
    padding: 0 !important;

    #itf-print-root,
    #itf-print-root * {
      font-family: "Times New Roman", Times, serif !important;
      color: #000 !important;
    }

    .row.invoice-preview {
      display: block !important;
      width: 210mm !important;
      max-width: 210mm !important;
      margin: 0 auto !important;

      > .itf-print-main {
        display: block !important;
        flex: 0 0 100% !important;
        width: 210mm !important;
        max-width: 210mm !important;
        margin: 0 auto !important;
        padding: 0 !important;
      }

      .invoice-preview-card {
        display: block !important;
        width: 100% !important;
        max-width: none !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        box-shadow: none !important;
        border: 0 !important;
      }
    }

    .invoice-actions {
      display: none !important;
    }
  }
}
</style>
